import { useAtom } from "jotai";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import "../sideMenu/style.scss";
import { Sidebar } from "react-pro-sidebar";
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { sideMenu } from "../../utils/atoms/sideMenuAtom";
import HelpCard from "../help/helpCard";
import HelpCardAccordion from "../help/helpCardAccordion";
import { Tabs, useMantineColorScheme } from "@mantine/core";
import json from "../../components/help/helpCardMenu.json";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";
import MGAAPI from "../../api/index";

const MGAAPIS = new MGAAPI();


const RTLSideMenu = ({ onFloatingChange }) => {
  const { t } = useTranslation();
  // Floating panel width/height state for resizing
  const [panelSize, setPanelSize] = useState({ width: 350, height: 500 });
  const resizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0, width: 350, height: 500 });

  // Resize handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    resizing.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    resizeStart.current = {
      x: clientX,
      y: clientY,
      width: panelSize.width,
      height: panelSize.height,
    };
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", handleResizeEnd);
    document.addEventListener("touchmove", handleResizing);
    document.addEventListener("touchend", handleResizeEnd);
  };

  const handleResizing = (e) => {
    e.preventDefault();
    if (!resizing.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - resizeStart.current.x;
    const dy = clientY - resizeStart.current.y;
    setPanelSize({
      width: Math.max(300, Math.min(800, resizeStart.current.width + dx)),
      height: Math.max(300, Math.min(1000, resizeStart.current.height + dy)),
    });
  };

  const handleResizeEnd = () => {
    resizing.current = false;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", handleResizeEnd);
    document.removeEventListener("touchmove", handleResizing);
    document.removeEventListener("touchend", handleResizeEnd);
  };
  const [collapseSideMenu, setCollapseSideMenu] = useAtom(collapseAtom);
  const [sideMenuShow, setSideMenu] = useAtom(sideMenu);
  const [componentDetails] = useAtom(componentsAtom);
  const [helpMenuData, setHelpMenuData] = useAtom(helpContextMenuAtom);
  const [isHelpLoading, setIsHelpLoading] = useState(true);
  const currentComponent = componentDetails?.currentComponent;
  const formFields = helpMenuData?.[currentComponent];

  const fetchHelpMenuData = () => {
    setIsHelpLoading(true);
    MGAAPIS.getContextMenu()
      .then((res) => {
        setHelpMenuData(res);
      })
      .catch((error) => {
        setHelpMenuData({});
        console.error("Error fetching help menu data:", error);
      })
      .finally(() => {
        setIsHelpLoading(false);
      });
  };

  useEffect(() => {
    fetchHelpMenuData();
  }, []);

  // Dynamically collect all accordion-genset-* sections for the current component
  let accordionSections = [];
  const componentData = helpMenuData?.[currentComponent];
  if (componentData) {
    accordionSections = Object.keys(componentData)
      .filter((key) => key.startsWith(`accordion-${currentComponent}`))
      .map((key) => ({ key, ...componentData[key] }));
  }
  const { colorScheme } = useMantineColorScheme();

  // Search state for fuzzy search
  const [searchTerm, setSearchTerm] = useState("");

  // Floating/docking state for draggable menu
  const [isFloating, setIsFloating] = useState(false);
  // Notify parent when floating state changes
  useEffect(() => {
    if (typeof onFloatingChange === "function") {
      onFloatingChange(isFloating);
    }
  }, [isFloating, onFloatingChange]);
  const [floatPos, setFloatPos] = useState({ x: window.innerWidth-100, y: 100 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragStartedFromDock = useRef(false);

  // Mouse event handlers for drag
  const handleDragStart = (e) => {
    // Only undock if not already floating
    if (!isFloating) {
      dragStartedFromDock.current = true;
      // Start at docked position
      setFloatPos({ x: window.innerWidth-100, y: 80 });
      setIsFloating(true);
    } else {
      dragStartedFromDock.current = false;
    }
    dragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragOffset.current = {
      x: clientX - floatPos.x,
      y: clientY - floatPos.y,
    };
    dragStartPos.current = { x: clientX, y: clientY };
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDragMove);
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDragMove = (e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // If just started from dock, only undock if moved enough
    if (dragStartedFromDock.current) {
      const dx = Math.abs(clientX - dragStartPos.current.x);
      const dy = Math.abs(clientY - dragStartPos.current.y);
      if (dx < 10 && dy < 10) return; // Don't undock until moved 10px
      dragStartedFromDock.current = false; // Only trigger once
    }
    let newX = clientX - dragOffset.current.x;
    let newY = clientY - dragOffset.current.y;
    setFloatPos({ x: newX, y: newY });
  };

  const handleDragEnd = () => {
    dragging.current = false;
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDragMove);
    document.removeEventListener("touchend", handleDragEnd);
    // If dragged to right edge, dock back
    // if (floatPos.x > window.innerWidth - 370) {
    //   setIsFloating(false);
    // }
  };

  // Handler to undock (float) the menu
  const handleUndock = () => {
    setIsFloating(true);
    setFloatPos({ x: window.innerWidth - 100, y: 80 });
  };

  // Fuzzy filter function for help cards and accordion cards
  function fuzzyMatch(str, term) {
    if (!term) return true;
    if (!str) return false;
    return str.toLowerCase().includes(term.toLowerCase());
  }

  // Filter accordion sections and form fields based on search
  const filteredAccordionSections = accordionSections.filter(
    (section) =>
      fuzzyMatch(section["component-title"], searchTerm) ||
      fuzzyMatch(section["component-subtitle"], searchTerm) ||
      fuzzyMatch(section["details"], searchTerm)
  );
  //   useEffect(()=>{
  //   if(isFloating){
  //     setFloatPos({ x: window.innerWidth-100, y: 80 });
  //   }
  // },[isFloating])
  const filteredFormFields = Object.keys(formFields || {})?.filter(
    (fieldKey) => {
      const field = formFields?.[fieldKey];
      return (
        fuzzyMatch(field.title, searchTerm) ||
        fuzzyMatch(field.description, searchTerm) ||
        fuzzyMatch(field.placeholder, searchTerm) ||
        fuzzyMatch(field.type, searchTerm)
      );
    }
  );

  const renderHelpCardSkeleton = (keyPrefix) =>
    Array.from({ length: 3 }).map((_, index) => (
      <div
        key={`${keyPrefix}-skeleton-${index}`}
        style={{
          maxWidth: "400px",
          margin: "20px auto",
          border: "1px solid rgba(120, 120, 120, 0.25)",
          borderRadius: 8,
          overflow: "hidden",
          minHeight: 170,
          background: colorScheme === "dark" ? "#1f2729" : "#fff",
        }}
      >
        {[1, 2, 3].map((row) => (
          <div
            key={`${keyPrefix}-skeleton-${index}-row-${row}`}
            style={{
              padding: "16px 12px",
              borderBottom: row !== 3 ? "1px solid rgba(120, 120, 120, 0.2)" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div
              style={{
                height: 14,
                width: row === 3 ? "32%" : "28%",
                borderRadius: 4,
                background: colorScheme === "dark" ? "#3b4548" : "#e5e7eb",
              }}
            />
            <div
              style={{
                height: 14,
                width: row === 1 ? "54%" : row === 2 ? "44%" : "62%",
                borderRadius: 4,
                background: colorScheme === "dark" ? "#4d5a5e" : "#d1d5db",
              }}
            />
          </div>
        ))}
      </div>
    ));

  // Render floating panel if isFloating, otherwise render Sidebar
  if (isFloating) {
    return (
      <div
        style={{
          position: "fixed",
          left: floatPos.x,
          top: floatPos.y,
          width: panelSize.width,
          minWidth: 300,
          maxWidth: 800,
          height: panelSize.height,
          minHeight: 700,
          maxHeight: 1000,
          zIndex: 9999,
                justifyContent: "space-between",
          boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
          borderRadius: 8,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          cursor: dragging.current ? "grabbing" : "default",
        }}
      >
        {/* Resize handle (bottom-right corner) */}
        <div
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 18,
            height: 18,
            cursor: "nwse-resize",
            zIndex: 10000,
            background: "transparent",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
          }}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#888" strokeWidth="2"><polyline points="4,18 18,18 18,4" /></svg>
        </div>
        {/* Drag handle */}
        <div
          style={{
            width: "100%",
            height: 32,
            background: colorScheme === "dark" ? "#232b2d" : "#e9ecef",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "grab",
            padding: "0 12px",
            fontWeight: 600,
            userSelect: "none",
            borderBottom: "1px solid #ccc",
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <span>{t('help.title')}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Close (X) button */}
                     <button
                  aria-label="help-dock-to-sidebar"
              style={{
                background: "none",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: 0,
              }}
              title="Dock to sidebar"
              onClick={(e) => {
                e.stopPropagation();
                setIsFloating(false);
              }}
            >
              {/* Dock icon (Material/Bootstrap SVG) */}
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 3v18" />
              </svg>
            </button>
            <button
              aria-label="help-close-menu"
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: 0,
              }}
              title="Close help"
              onClick={(e) => {
                e.stopPropagation();
                setSideMenu((prev) => ({ showMenu: !prev.showMenu }));
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
   
          </div>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {/* Sticky header and tabs wrapper - fixed to top of floating panel */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              background: colorScheme === "dark" ? "#1f2729" : "white",
              boxShadow: "rgba(90, 116, 148, 0.1) 0px 2px 4px",
            }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                marginTop: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "2.5rem",
                boxShadow: "rgba(90, 116, 148, 0.3) 0px 4px 4px",
                background: colorScheme === "dark" ? "#1f2729" : "white",
              }}
              data-testid="help-card"
            >
              {t('help.title')}
            </div>
            <div
              style={{
                background: colorScheme === "dark" ? "#1f2729" : "white",
              }}
            >
              <Tabs
                style={{
                  borderBottom: "none",
                  background: colorScheme === "dark" ? "#1f2729" : "white",
                }}
                color="teal"
                defaultValue="guide"
              >
                <Tabs.List
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    fontSize: "14px",
                    backgroundColor:
                      colorScheme === "dark" ? "#1f2729" : "white",
                  }}
                >
                  <Tabs.Tab
                    value="guide"
                    style={{ fontSize: "14px", fontWeight: "bold" }}
                  >
                    {t('input.guide')}
                  </Tabs.Tab>
                </Tabs.List>
                {/* Search bar below Input Guide tab */}
                <div
                  style={{
                    padding: "8px 16px",
                    background: colorScheme === "dark" ? "#1f2729" : "white",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <input
                    type="text"
                    placeholder={t('help.search.placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      background: colorScheme === "dark" ? "#232b2d" : "#fff",
                      color: colorScheme === "dark" ? "#fff" : "#222",
                    }}
                  />
                </div>
              </Tabs>
            </div>
          </div>
          {/* Content below sticky header */}
          <Tabs
            style={{ marginTop: 0, borderBottom: "none" }}
            color="teal"
            defaultValue="guide"
          >
            <Tabs.Panel value="guide">
              <div
                style={{
                  backgroundColor:
                    colorScheme === "dark"
                      ? "var(--se-result-standard)"
                      : "white",
                }}
              >
                {isHelpLoading ? (
                  renderHelpCardSkeleton("floating")
                ) : (
                  <>
                    {filteredAccordionSections.length > 0 &&
                      filteredAccordionSections.map((section) => (
                        <HelpCardAccordion
                          id={section.key}
                          key={section.key}
                          componentTitle={section["component-title"]}
                          componentSubtitle={section["component-subtitle"]}
                          details={section["details"]}
                        />
                      ))}
                    {filteredFormFields?.length > 0 ? (
                      filteredFormFields?.map((fieldKey) => {
                        const field = formFields[fieldKey];
                        return (
                          <HelpCard
                            id={fieldKey}
                            key={fieldKey}
                            title={field.title}
                            description={
                              field.description && typeof field.description === 'string'
                                ? field.description.split(/\n/g).map((line, idx, arr) =>
                                  idx < arr.length - 1 ? <>{line}<br /></> : line
                                )
                                : field.description
                            }
                            placeholder={field.placeholder}
                            type={field.type}
                          />
                        );
                      })
                    ) : (
                      <span className="d-flex justify-content-center">
                        {t('no.content')}
                      </span>
                    )}
                  </>
                )}
              </div>
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    );
  }

  // Docked sidebar (default)
  return (
    <Sidebar
      className={`sidebar-right sidebar ${
        !collapseSideMenu.rightMenu ? "collapsed" : "expanded"
      }`}
      rootStyles={{
        top: "108px",
        position: "fixed",
        right: 0,
        width: "350px",
        minWidth: "350px",
        height: "103vh !important",
        border: "transparent",
        zIndex: 99,
      }}
      toggled={true}
      collapsed={false}
      style={{ zIndex: 99 }}
    >
      <div
        className={`sidebar-content ${
          colorScheme === "dark" ? "dark-mode" : "light-mode"
        }`}
        style={{
          scrollbarWidth: "none",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
        onWheel={(e) => {
          // Prevent wheel events from bubbling to the main page
          e.stopPropagation();
        }}
      >
        <div>
          {/* Sticky header and tabs wrapper - fixed to top of sidebar */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              background: colorScheme === "dark" ? "#1f2729" : "white",
              boxShadow: "rgba(90, 116, 148, 0.1) 0px 2px 4px",
            }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                marginTop: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                height: "2.5rem",
                boxShadow: "rgba(90, 116, 148, 0.3) 0px 4px 4px",
                background: colorScheme === "dark" ? "#1f2729" : "white",
                paddingRight: 8,
                paddingLeft: 16,
                position: "relative",
              }}
              data-testid="help-card"
            >
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  pointerEvents: "none",
                  width: "100%",
                }}
              >
                {t('help.title')}
              </span>
              {isFloating ? (
                <button
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 18,
                    cursor: "pointer",
                    zIndex: 1,
                  }}
                  title="Dock to sidebar"
                  onClick={() => setIsFloating(false)}
                >
                  ⬅
                </button>
              ) : (
                <span
                  style={{
                    fontSize: 18,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    zIndex: 1,
                  }}
                  title="Undock panel"
                  role="button"
                  tabIndex={0}
                  aria-label="Undock panel"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsFloating(true);
                    }
                  }}
                  onClick={() => setIsFloating(true)}
                  // onMouseDown={handleDragStart}
                  // onTouchStart={handleDragStart}
                >
                  {/* Overlapping squares (Windows undock/floating icon, with hidden intersecting lines) */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="7" y="7" width="12" height="12" rx="2" />
                    <rect x="3" y="3" width="12" height="12" rx="2" />
                    <rect x="7" y="7" width="12" height="12" rx="2" fill="white" stroke="none" />
                  </svg>
                </span>
              )}
              {/* Close (X) button on docked header */}
              <button
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 18,
                  cursor: "pointer",
                  zIndex: 1,
                  marginLeft: 8,
                }}
                title="Close help"
                onClick={() => setSideMenu((prev) => ({ showMenu: !prev.showMenu }))}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {/* <button
              style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer" }}
              title="Float panel"
              onClick={handleUndock}
            >
              ⬏
            </button> */}
            <div
              style={{
                background: colorScheme === "dark" ? "#1f2729" : "white",
              }}
            >
              <Tabs
                style={{
                  borderBottom: "none",
                  background: colorScheme === "dark" ? "#1f2729" : "white",
                }}
                color="teal"
                defaultValue="guide"
              >
                <Tabs.List
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    fontSize: "14px",
                    backgroundColor:
                      colorScheme === "dark" ? "#1f2729" : "white",
                  }}
                >
                  <Tabs.Tab
                    value="guide"
                    style={{ fontSize: "14px", fontWeight: "bold" }}
                  >
                    {t('input.guide')}
                  </Tabs.Tab>
                </Tabs.List>
                {/* Search bar below Input Guide tab */}
                <div
                  style={{
                    padding: "8px 16px",
                    background: colorScheme === "dark" ? "#1f2729" : "white",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <input
                    type="text"
                    placeholder={t('help.search.placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      background: colorScheme === "dark" ? "#232b2d" : "#fff",
                      color: colorScheme === "dark" ? "#fff" : "#222",
                    }}
                  />
                </div>
              </Tabs>
            </div>
          </div>
          {/* Content below sticky header */}
          <Tabs
            style={{ marginTop: 0, borderBottom: "none" }}
            color="teal"
            defaultValue="guide"
          >
            <Tabs.Panel value="guide">
              <div
                style={{
                  backgroundColor:
                    colorScheme === "dark"
                      ? "var(--se-result-standard)"
                      : "white",
                }}
              >
                {isHelpLoading ? (
                  renderHelpCardSkeleton("docked")
                ) : (
                  <>
                    {filteredAccordionSections.length > 0 &&
                      filteredAccordionSections.map((section) => (
                        <HelpCardAccordion
                          id={section.key}
                          key={section.key}
                          componentTitle={section["component-title"]}
                          componentSubtitle={section["component-subtitle"]}
                          details={section["details"]}
                        />
                      ))}
                    {filteredFormFields?.length > 0 ? (
                      filteredFormFields?.map((fieldKey) => {
                        const field = formFields[fieldKey];
                        return (
                          <HelpCard
                            id={fieldKey}
                            key={fieldKey}
                            title={field.title}
                            description={
                              field.description && typeof field.description === 'string'
                                ? field.description.split(/\n/g).map((line, idx, arr) =>
                                  idx < arr.length - 1 ? <>{line}<br /></> : line
                                )
                                : field.description
                            }
                            placeholder={field.placeholder}
                            type={field.type}
                          />
                        );
                      })
                    ) : (
                      <span className="d-flex justify-content-center">
                        {t('no.content')}
                      </span>
                    )}
                  </>
                )}
              </div>
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    </Sidebar>
  );
};

export default RTLSideMenu;
